import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { publicApi, adminApi } from '../../api/client';
import { toast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const empty = {
  slug: '', name: '', tag: '', days: '7 Days',
  price: 0, price_label: 'Start from',
  image: '', gallery: [], short_desc: '', description: '',
  highlights: [], includes: [], excludes: [], itinerary: [],
  featured: false, order: 0,
};

const AdminPackages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await publicApi.getPackages({ limit: 500 })); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (i) => { setEditing(i); setForm({ ...i, gallery: i.gallery || [], highlights: i.highlights || [], includes: i.includes || [], excludes: i.excludes || [], itinerary: i.itinerary || [] }); setOpen(true); };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updNum = (k) => (e) => setForm({ ...form, [k]: e.target.value === '' ? 0 : parseFloat(e.target.value) });
  const updList = (k) => (e) => setForm({ ...form, [k]: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.name) };
      if (editing) await adminApi.updatePackage(editing.id, payload);
      else await adminApi.createPackage(payload);
      toast({ title: editing ? 'Package updated' : 'Package created' });
      setOpen(false); load();
    } catch (err) { toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try { await adminApi.deletePackage(item.id); load(); toast({ title: 'Deleted' }); } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-[#304b71]">Tour Packages</h1>
          <p className="text-slate-500 text-sm mt-1">Manage Egypt tour packages and Nile cruise deals.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={startCreate} className="bg-[#304b71] hover:bg-[#6a8aa4] text-white"><Plus size={16} className="mr-1" /> New Package</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-[#304b71] text-xl">{editing ? 'Edit Package' : 'New Package'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input required value={form.name} onChange={upd('name')} className="mt-1" /></div>
                <div><Label>Slug (auto)</Label><Input value={form.slug} onChange={upd('slug')} placeholder={slugify(form.name)} className="mt-1" /></div>
                <div><Label>Days *</Label><Input required value={form.days} onChange={upd('days')} className="mt-1" /></div>
                <div><Label>Tag</Label><Input value={form.tag} onChange={upd('tag')} className="mt-1" /></div>
                <div><Label>Price (USD) *</Label><Input required type="number" step="0.01" value={form.price} onChange={updNum('price')} className="mt-1" /></div>
                <div><Label>Price Label</Label><Input value={form.price_label} onChange={upd('price_label')} className="mt-1" /></div>
                <div className="md:col-span-2"><Label>Main Image URL *</Label><Input required value={form.image} onChange={upd('image')} className="mt-1" /></div>
                <div className="md:col-span-2"><Label>Short Description</Label><Textarea rows={2} value={form.short_desc} onChange={upd('short_desc')} className="mt-1" /></div>
                <div className="md:col-span-2"><Label>Full Description</Label><Textarea rows={4} value={form.description} onChange={upd('description')} className="mt-1" /></div>
                <div className="md:col-span-2"><Label>Highlights (one per line)</Label><Textarea rows={3} value={(form.highlights || []).join('\n')} onChange={updList('highlights')} className="mt-1" /></div>
                <div><Label>Included (one per line)</Label><Textarea rows={4} value={(form.includes || []).join('\n')} onChange={updList('includes')} className="mt-1" /></div>
                <div><Label>Excluded (one per line)</Label><Textarea rows={4} value={(form.excludes || []).join('\n')} onChange={updList('excludes')} className="mt-1" /></div>
                <div><Label>Order</Label><Input type="number" value={form.order} onChange={updNum('order')} className="mt-1" /></div>
                <div className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /><Label>Featured on homepage</Label></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" disabled={saving} className="bg-[#304b71] hover:bg-[#6a8aa4] text-white">{saving ? <Loader2 className="animate-spin" size={16} /> : 'Save'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#304b71]" /></div> :
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-[#f6f8fa]">
              <tr><th className="text-left px-5 py-3">Package</th><th className="text-left">Days</th><th className="text-left">Price</th><th className="text-left">Featured</th><th className="text-right px-5">Actions</th></tr>
            </thead>
            <tbody>{items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="px-5 py-3"><div className="flex items-center gap-3"><img src={c.image} alt="" className="w-14 h-14 object-cover rounded-lg" /><div><div className="font-serif text-[#304b71]">{c.name}</div><div className="text-xs text-slate-500">{c.slug}</div></div></div></td>
                <td className="text-slate-600">{c.days}</td>
                <td className="text-slate-700 font-semibold">${c.price?.toLocaleString()}</td>
                <td>{c.featured && <span className="bg-[#304b71] text-white px-2 py-0.5 rounded text-xs">Featured</span>}</td>
                <td className="text-right px-5 space-x-1"><Button size="sm" variant="outline" onClick={() => startEdit(c)}><Pencil size={14} /></Button><Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => del(c)}><Trash2 size={14} /></Button></td>
              </tr>
            ))}</tbody>
          </table>
        }
      </div>
    </div>
  );
};

export default AdminPackages;
